import React, { useEffect, useState } from "react";
import { Plus, X, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";

export default function Team() {
  const [team, setTeam] = useState([]);
  const [showPanel, setShowPanel] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  const load = async () => { const { data } = await api.get("/team"); setTeam(data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fullPhone = form.phone.startsWith("+") ? form.phone : `+91${form.phone.replace(/\D/g,"")}`;
      const { data } = await api.post("/team/invite", { name: form.name, phone: fullPhone, email: form.email || undefined });
      const fullUrl = `${window.location.origin}/invite/${data.invite_token}`;
      setLastInvite({ ...data, full_url: fullUrl });
      toast.success(`${form.name} invited! Send them the link.`);
      setForm({ name: "", phone: "", email: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display">My Team</h1>
          <p className="text-sm text-gray-500 mt-1">Add agents who'll create deals for your agency.</p>
        </div>
        <button onClick={() => setShowPanel(true)} className="bg-[#F97316] hover:bg-[#EA580C] btn-press text-white px-4 py-2.5 rounded-lg text-sm font-medium inline-flex items-center gap-2" data-testid="add-agent-btn"><Plus className="h-4 w-4" /> Add Agent</button>
      </div>

      <div className="card-base">
        {team.length === 0 ? (
          <div className="p-10 text-center text-gray-500 text-sm">No agents yet. Click "Add Agent" to invite your first one.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[11px] text-gray-500 uppercase tracking-wider bg-gray-50">
              <tr><th className="text-left px-5 py-3">Agent</th><th className="text-left px-5 py-3">Phone</th><th className="text-left px-5 py-3">Active deals</th><th className="text-left px-5 py-3">Total deals</th><th className="text-left px-5 py-3">Joined</th><th className="text-left px-5 py-3">Status</th></tr>
            </thead>
            <tbody>
              {team.map(a => (
                <tr key={a.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-5 py-3 flex items-center gap-2">
                    <div className="h-7 w-7 rounded-full bg-[#F97316] text-white flex items-center justify-center text-xs font-semibold">{a.name?.[0]?.toUpperCase()}</div>
                    {a.name}
                  </td>
                  <td className="px-5 py-3 text-gray-600">{a.phone}</td>
                  <td className="px-5 py-3">{a.deals_active}</td>
                  <td className="px-5 py-3">{a.deals_total}</td>
                  <td className="px-5 py-3 text-gray-600">{formatDate(a.created_at)}</td>
                  <td className="px-5 py-3"><span className={`pill ${a.verified ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{a.verified ? "Active" : "Pending"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Slide-over */}
      {showPanel && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => { setShowPanel(false); setLastInvite(null); }} />
          <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl p-6 overflow-y-auto" data-testid="invite-panel">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold font-display">Add a new agent</h2>
              <button onClick={() => { setShowPanel(false); setLastInvite(null); }} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-gray-500 mt-1">They'll get a WhatsApp invite to join your team.</p>

            {lastInvite ? (
              <div className="mt-6 rounded-lg bg-emerald-50 p-4 border border-emerald-200">
                <div className="text-sm font-medium text-emerald-800">Invite created!</div>
                <div className="mt-2 text-xs text-gray-600">Share this link with the agent:</div>
                <div className="mt-2 flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                  <code className="text-xs flex-1 truncate">{lastInvite.full_url}</code>
                  <button onClick={() => { navigator.clipboard.writeText(lastInvite.full_url); toast.success("Link copied"); }} className="text-[#F97316] hover:text-indigo-700"><Copy className="h-4 w-4" /></button>
                </div>
                <button onClick={() => setLastInvite(null)} className="mt-4 w-full bg-[#F97316] text-white rounded-lg py-2 text-sm font-medium">Add another</button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="text-sm font-medium">Full name</label>
                  <input data-testid="invite-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100" />
                </div>
                <div>
                  <label className="text-sm font-medium">Mobile number</label>
                  <div className="mt-1 flex items-stretch border border-gray-300 rounded-lg overflow-hidden">
                    <span className="px-3 flex items-center bg-gray-50 text-sm text-gray-500 border-r">+91</span>
                    <input data-testid="invite-phone" required value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="flex-1 px-3 py-2.5 text-sm focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Email <span className="text-gray-400">(optional)</span></label>
                  <input data-testid="invite-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none" />
                </div>
                <button data-testid="invite-submit" disabled={busy} className="w-full h-12 bg-[#F97316] hover:bg-[#EA580C] btn-press text-white rounded-lg font-medium disabled:opacity-50">{busy ? "Sending…" : "Send Invite"}</button>
              </form>
            )}
          </div>
        </>
      )}
    </div>
  );
}
